import React, { Fragment, useRef, useMemo, useState } from "react";
import { Box, Paper, Typography, Divider, BoxProps } from "@material-ui/core";

import { APIError } from "../../components/APIError";
import { PanelHeader } from "../../components/PanelHeader";
import { MeterPointIcon } from "../Properties/components/MeterPointIcon";
import { DateRangeLabel } from "../Properties/components/DateRangeLabel";
import { PropertyHeader } from "../Properties/components/PropertyHeader";
import { useAccountProperties } from "../../api/hooks/useAccountProperties";
import { AlertBlock } from "../../components/AlertBlock";
import { PropertyLoader } from "../Properties/components/PropertyLoader";
import { AgreementInfo } from "../../api/services";
import { useParams, useRouteMatch, useLocation } from "react-router-dom";
import { useAgreement } from "../Properties/hooks/useAgreement";
import { AgreementTariff } from "../Properties/components/AgreementTariff";
import { MeterPointInfo } from "../../api/meters";
import { useMeterPointAgreements } from "../../api/hooks/useMeterPointAgreements";
import { DialogLoader } from "../../components/Loader";
import { PageLink, ButtonLink } from "../../components/CustomLink";
import { TextBox } from "../../components/TextBox";
import { ScrollToElementById } from "../../components/ScrollToElement";
import { createAgreementHistory } from "./utils";
import { AgreementStatusChip } from "./components/AgreementStatusChip";

const MeterPointContainer: React.FC<{
  meterPoint: MeterPointInfo;
}> = ({ meterPoint, children }) => {
  const { type, identifier, supplyStartDt, supplyEndDt, id } = meterPoint;
  return (
    <Box
      id={id}
      maxWidth="860px"
      component={Paper}
      mb={3}
      overflow="hidden"
      data-testid="AgreementHistoryView__MeterPoint"
    >
      <PanelHeader
        icon={<MeterPointIcon type={type} />}
        title={identifier}
        gutter={0.5}
        p={2}
        ml={2}
      />
      <Divider />
      <Box mx={4} py={2.5}>
        <DateRangeLabel
          title="Supply period"
          from={supplyStartDt}
          to={supplyEndDt}
          variant="body1"
        />
        <Box mt={4} mb={1}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

const AgreementContainer: React.FC<{ title: string } & BoxProps> = ({
  title,
  children,
  ...props
}) => (
  <Box {...props}>
    <Box mb={1.5}>
      <Typography variant="overline">{title}</Typography>
    </Box>
    <Divider />
    <Box mt={2}>{children}</Box>
  </Box>
);

const AgreementRow: React.FC<{ agreement: AgreementInfo } & BoxProps> = ({
  agreement,
  ...props
}) => {
  const { name: agreementName, fromDttm, id, cancelledDttm } = agreement;
  const { rates, charge, endDttm } = useAgreement(agreement);
  const { url } = useRouteMatch();
  return (
    <Box display="flex" flexDirection="row" {...props}>
      <Box
        display="flex"
        flexDirection="column"
        flexBasis="25%"
        mr={2}
        pb={2.5}
      >
        <TextBox isStruckThrough={!!cancelledDttm} isGrey={!!cancelledDttm}>
          <DateRangeLabel from={fromDttm} to={endDttm} variant="body1" />
        </TextBox>
        <AgreementStatusChip agreement={agreement} />
      </Box>
      <Box display="flex" flexDirection="column" flexBasis="60%" mr={2}>
        <TextBox isStruckThrough={!!cancelledDttm} isGrey={!!cancelledDttm}>
          <Typography variant="subtitle1">{agreementName}</Typography>
        </TextBox>
        <AgreementTariff rates={rates} charge={charge} mt={1} />
      </Box>
      <PageLink
        path={`${url}/agreements/${id}`}
        text="View details"
        data-testid="AgreementRow__Details"
      />
    </Box>
  );
};

// Agreements are grouped by type
const AgreementGroup: React.FC<
  {
    type: "Future" | "Past" | "Current";
    agreements: AgreementInfo[];
    maxItems?: number;
  } & BoxProps
> = ({ type, agreements, maxItems, ...props }) => {
  const title = `${type} Agreement`;
  // As per design, current agreement doesn't display agreement count
  const suffix =
    type === "Current"
      ? ""
      : agreements.length > 1
      ? `s (${agreements.length})`
      : ` (${agreements.length})`;
  const toDisplay =
    maxItems !== undefined ? agreements.slice(0, maxItems) : agreements;

  if (agreements.length === 0) {
    // show a message if no current agreements
    // or display nothing for future and past agreements
    if (type === "Current") {
      return (
        <AgreementContainer title={`${title}${suffix}`} {...props}>
          <Box mb={2}>No current agreement.</Box>
        </AgreementContainer>
      );
    } else {
      return null;
    }
  }
  return (
    <AgreementContainer title={`${title}${suffix}`} {...props}>
      <>
        {toDisplay.map((agreement, index) => (
          <Fragment key={agreement.id}>
            {index > 0 && <Divider />}
            <AgreementRow agreement={agreement} key={agreement.id} mt={2.5} />
          </Fragment>
        ))}
      </>
    </AgreementContainer>
  );
};

const AgreementList: React.FC<{
  agreements: AgreementInfo[];
}> = ({ agreements }) => {
  const [hideOlderAgreements, setHideOlderAgreements] = useState(true);
  const { future, current, past } = useMemo(
    () => createAgreementHistory(agreements),
    [agreements],
  );
  const { taxDescription } = useAgreement(agreements[0]);

  const { toggleButtonLabel, maxPastAgreements } = useMemo(() => {
    const DEFAULT_DISPLAY_COUNT = 3;
    if (past.length <= DEFAULT_DISPLAY_COUNT) {
      return {
        toggleButtonLabel: null,
        maxPastAgreements: undefined,
      };
    } else if (hideOlderAgreements) {
      return {
        toggleButtonLabel: "View older agreements",
        maxPastAgreements: DEFAULT_DISPLAY_COUNT,
      };
    } else {
      return {
        toggleButtonLabel: "Hide older agreements",
        maxPastAgreements: undefined,
      };
    }
  }, [hideOlderAgreements, past]);

  return (
    <>
      <AgreementGroup
        type="Future"
        agreements={future}
        mt={4}
        data-testid="AgreementContainer__Future"
      />
      {future.length && (current.length || past.length) ? <Divider /> : null}
      <AgreementGroup
        type="Current"
        agreements={current}
        mt={4}
        data-testid="AgreementContainer__Current"
      />
      {(future.length || current.length) && past.length ? <Divider /> : null}
      <AgreementGroup
        type="Past"
        agreements={past}
        maxItems={maxPastAgreements}
        mt={4}
        data-testid="AgreementContainer__Past"
      />
      <Divider />
      <Box
        mt={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        data-testid="AgreementList--Footer"
      >
        {toggleButtonLabel ? (
          <ButtonLink
            text={toggleButtonLabel}
            data-testid="AgreementList__Toggle--button"
            handleClick={() => setHideOlderAgreements(!hideOlderAgreements)}
          />
        ) : (
          <div />
        )}
        <Typography component="span" variant="caption" color="textPrimary">
          {taxDescription}
        </Typography>
      </Box>
    </>
  );
};

const AgreementHistoryMeterPointPanel: React.FC<{
  meterPoint: MeterPointInfo;
}> = ({ meterPoint }) => {
  const { accountNumber } = useParams();
  const { id: meterPointId } = meterPoint;
  const { status, error, data: agreements } = useMeterPointAgreements(
    accountNumber,
    meterPointId,
  );

  return (
    <MeterPointContainer meterPoint={meterPoint}>
      {status === "loading" ? (
        <DialogLoader data-testid="AgreementHistoryMeterPointPanel--Loader" />
      ) : error ? (
        <APIError
          title="We could not load the agreements for this meter point."
          messages={[(error as Error).message]}
          data-testid="AgreementHistoryMeterPointPanel--Error"
        />
      ) : !agreements?.length ? (
        <AlertBlock
          variant="info"
          data-testid="AgreementHistoryMeterPointPanel--Empty"
          messages={["No agreements found for this meter point."]}
          isClosable={false}
        />
      ) : (
        <AgreementList agreements={agreements} />
      )}
    </MeterPointContainer>
  );
};

export const AgreementHistoryView: React.FC = () => {
  const { accountNumber } = useParams();
  const { data, status, error } = useAccountProperties(accountNumber);
  const properties = data?.results;

  const ref = useRef<HTMLDivElement | null>(null);
  const { state } = useLocation();
  const { meterPointId } = (state as any) || {};

  return error ? (
    <APIError
      title="We could not load agreements for this account."
      data-testid="AgreementHistoryView__Error"
    />
  ) : status === "loading" ? (
    <PropertyLoader data-testid="AgreementHistoryView__Loader" />
  ) : !properties || properties.length === 0 ? (
    <AlertBlock
      variant="info"
      isClosable={false}
      data-testid="AgreementHistoryView__Empty"
      messages={["No agreements found on this account."]}
    />
  ) : (
    <div ref={ref}>
      {properties.map((property) => {
        const { address, meterPoints, id } = property;
        return (
          <Fragment key={id}>
            <PropertyHeader
              address={address}
              gutter={0.5}
              mt={-0.25}
              mb={1}
              data-testid="AgreementHistoryView__Property--Header"
            />
            {meterPoints.map((mp) => (
              <AgreementHistoryMeterPointPanel meterPoint={mp} key={mp.id} />
            ))}
          </Fragment>
        );
      })}
      <ScrollToElementById container={ref.current} id={meterPointId} />
    </div>
  );
};
